import React, { useState } from 'react';

import styled from 'styled-components';

import { ContentEntry } from '../services/ContentConflictResolver';

interface TabbedContentProps {
  entries: ContentEntry[];
  activeEntryId: string;
  onTabChange: (entryId: string) => void;
  area: string;
  className?: string;
}

/**
 * Component that renders multiple content entries as tabs when conflicts occur.
 * This provides a user-friendly way to access all content that wants to render
 * in the same panel area.
 */
export const TabbedContent: React.FC<TabbedContentProps> = ({ entries, activeEntryId, onTabChange, area, className }) => {
  const [isTabListCollapsed, setIsTabListCollapsed] = useState(false);

  console.log(`🟦 [TabbedContent] RENDERING START - area=${area}, entries=${entries.length}, activeEntryId=${activeEntryId}`);
  console.log(
    `🟦 [TabbedContent] Entry details:`,
    entries.map((e) => ({ id: e.id, source: e.source, priority: e.priority })),
  );

  const activeEntry = entries.find((entry) => entry.id === activeEntryId) || entries[0];

  if (!activeEntry) {
    console.warn(`[TabbedContent] No active entry found for area ${area}`);
    return null;
  }

  // Single entry - render directly without tabs
  if (entries.length === 1) {
    console.log(`🟡 [TabbedContent] Single entry mode for area=${area}, rendering without tabs`);
    return <ContentWrapper className={className}>{activeEntry.content}</ContentWrapper>;
  }

  console.log(`🟢 [TabbedContent] Multiple entries (${entries.length}) - rendering with tabs`);

  // Generate tab labels
  const getTabLabel = (entry: ContentEntry): string => {
    // Try to extract meaningful label from source or metadata
    if (entry.metadata?.label) {
      return entry.metadata.label as string;
    }

    if (entry.source) {
      return entry.source.replace(/([A-Z])/g, ' $1').trim(); // CamelCase to words
    }

    // Fallback to truncated ID
    return entry.id.length > 15 ? `${entry.id.slice(0, 12)}...` : entry.id;
  };

  // Sort entries by priority (highest first) then by timestamp (newest first)
  const sortedEntries = [...entries].sort((a, b) => {
    if (a.priority !== b.priority) {
      return b.priority - a.priority;
    }
    return b.timestamp - a.timestamp;
  });

  return (
    <TabbedContainer className={className}>
      <TabsHeader $collapsed={isTabListCollapsed}>
        <TabsList>
          {sortedEntries.map((entry) => (
            <Tab
              key={entry.id}
              $active={entry.id === activeEntryId}
              $priority={entry.priority}
              onClick={() => onTabChange(entry.id)}
              title={`Source: ${entry.source || 'Unknown'}\nPriority: ${entry.priority}\nTimestamp: ${new Date(entry.timestamp).toLocaleTimeString()}`}
            >
              <TabLabel>{getTabLabel(entry)}</TabLabel>
              {entry.priority > 0 && <PriorityIndicator $priority={entry.priority}>{entry.priority}</PriorityIndicator>}
            </Tab>
          ))}
        </TabsList>

        <TabControls>
          <CollapseButton onClick={() => setIsTabListCollapsed(!isTabListCollapsed)} title={isTabListCollapsed ? 'Show tabs' : 'Hide tabs'}>
            {isTabListCollapsed ? '⮟' : '⮝'}
          </CollapseButton>

          <InfoButton title={`${entries.length} content items competing for ${area} area`}>ⓘ</InfoButton>
        </TabControls>
      </TabsHeader>

      <ContentArea>{activeEntry.content}</ContentArea>
    </TabbedContainer>
  );
};

// Styled Components using proper theme tokens
const TabbedContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  background: ${({ theme }) => theme.colors.palette.white};
  border: 1px solid ${({ theme }) => theme.colors.borderBase};
  border-radius: ${({ theme }) => theme.radii.base};
`;

const TabsHeader = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.palette.grayscale[0]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  padding: ${({ theme }) => theme.space.spacing10};
  gap: ${({ theme }) => theme.space.spacing10};
  flex-shrink: 0;
  min-height: 40px;

  ${({ $collapsed, theme }) =>
    $collapsed &&
    `
    height: 32px;
    padding: ${theme.space.spacing10} ${theme.space.spacing10};
  `}
`;

const TabsList = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing10};
  flex: 1;
  overflow-x: auto;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $active: boolean; $priority: number }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};
  padding: ${({ theme }) => `${theme.space.spacing20} ${theme.space.spacing30}`};
  border: 1px solid ${({ theme }) => theme.colors.borderBase};
  border-radius: ${({ theme }) => theme.radii.base};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.palette.white)};
  color: ${({ theme, $active }) => ($active ? theme.colors.palette.white : theme.colors.textPrimary)};
  cursor: pointer;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  font-family: ${({ theme }) => theme.fontFamilies.body};
  line-height: ${({ theme }) => theme.lineHeights.footnote};
  transition: all 0.2s ease;
  min-height: 32px;

  /* Priority-based styling */
  ${({ $priority, theme }) => {
    if ($priority >= 2) {
      return `border-color: ${theme.colors.warning}; border-width: 2px;`;
    }
    if ($priority >= 1) {
      return `border-color: ${theme.colors.primary}; border-width: 2px;`;
    }
    return '';
  }}

  &:hover {
    background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.palette.grayscale[0])};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`;

const TabLabel = styled.span`
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PriorityIndicator = styled.span<{ $priority: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.palette.white};

  background: ${({ $priority, theme }) => {
    if ($priority >= 3) return theme.colors.error;
    if ($priority >= 2) return theme.colors.warning;
    if ($priority >= 1) return theme.colors.primary;
    return theme.colors.textSecondary;
  }};
`;

const TabControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing10};
  flex-shrink: 0;
`;

const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid ${({ theme }) => theme.colors.borderBase};
  border-radius: ${({ theme }) => theme.radii.base};
  background: ${({ theme }) => theme.colors.palette.grayscale[0]};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.footnote};

  &:hover {
    background: ${({ theme }) => theme.colors.palette.grayscale[1] || theme.colors.palette.grayscale[0]};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const InfoButton = styled(CollapseButton)`
  cursor: help;
`;

const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0;
  background: ${({ theme }) => theme.colors.palette.white};

  /* Ensure content fills available space */
  > * {
    min-height: 100%;
  }
`;

const ContentWrapper = styled.div`
  flex: 1 1 0;
  min-height: 0;
  background: ${({ theme }) => theme.colors.palette.white};
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
